import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Paper, Typography } from "@mui/material";
import { FC } from "react";

// import { ReactComponent as ForgotPasswordImage } from "../../assets/images/forgot-password.svg";
import ForgotPasswordImage from "../../assets/images/forgot-password.png";

interface IForgotPasswordDialogProps {
	open: boolean;
	onClose: () => void;
}

const ForgotPasswordDialog: FC<IForgotPasswordDialogProps> = (props) => {
	const { open, onClose } = props;

	return (
		<Dialog 
			open={open} 
			onClose={onClose} 
			fullWidth 
			maxWidth="lg"
			sx={{
				"& .MuiDialog-paper": {
					margin: { xs: "16px", sm: "24px" },
					maxHeight: { xs: "calc(100vh - 32px)", sm: "calc(100vh - 48px)" },
					width: { xs: "calc(100vw - 32px)", sm: "calc(100vw - 48px)" }
				}
			}}
		>
			<DialogTitle
				textAlign="center"
				sx={{
					background: "#5b4ebf",
					color: "white",
					position: "relative",
					padding: { xs: "12px 16px 8px 16px", sm: "16px 20px 12px 20px" },
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<Typography 
						variant="h6" 
						sx={{ 
							fontWeight: 600,
							fontSize: { xs: "1.125rem", sm: "1.25rem" }
						}}
					>
						Reset Your Password
					</Typography>
				</Box>
				<Box sx={{ position: "absolute", top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 } }}>
					<IconButton
						onClick={onClose}
						size="small"
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: { xs: "4px", sm: "6px" },
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent 
				classes={{ root: "dialog-content-root" }}
				sx={{ 
					padding: { xs: "16px", sm: "24px" },
					"&.dialog-content-root": {
						padding: { xs: "16px", sm: "24px" }
					}
				}}
			>
				<Grid container spacing={{ xs: 1, sm: 2 }}>
					<Grid item xs={12}>
						<Paper
							elevation={0}
							sx={{
								p: { xs: 2, sm: 3 },
								borderRadius: "16px",
								background: "#f8f9fa",
								border: "1px solid rgba(102, 126, 234, 0.1)"
							}}
						>
							<Typography
								variant="body1"
								fontWeight={500}
								color="text.primary"
								sx={{ 
									lineHeight: 1.6,
									fontSize: { xs: "0.875rem", sm: "1rem" }
								}}
								textAlign="center"
							>
								No worries, we're here to help! Please follow these steps to change your password and secure access to
								your account.
							</Typography>
						</Paper>
					</Grid>

					<Grid item xs={12} md={7}>
						<Typography
							variant="h5"
							fontWeight={700}
							sx={{
								mb: { xs: 2, sm: 3 },
								color: "#667eea",
								fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" }
							}}
						>
							Steps to Change the Password
						</Typography>

						<Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2 } }}>
							<Paper
								elevation={0}
								sx={{
									p: { xs: 2, sm: 3 },
									borderRadius: "16px",
									background: "white",
									border: "1px solid rgba(102, 126, 234, 0.1)",
									transition: "all 0.3s ease",
									"&:hover": {
										boxShadow: "0 8px 32px rgba(102, 126, 234, 0.1)",
										transform: "translateY(-2px)"
									}
								}}
							>
								<Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1.5, sm: 2 } }}>
									<Box
										sx={{
											width: { xs: 32, sm: 40 },
											height: { xs: 32, sm: 40 },
											borderRadius: "50%",
											background: "#667eea",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											flexShrink: 0,
											boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)"
										}}
									>
										<Typography 
											variant="h6" 
											color="white" 
											fontWeight={700}
											sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
										>
											1
										</Typography>
									</Box>

									<Box>
										<Typography 
											variant="h6" 
											fontWeight={600} 
											color="text.primary" 
											sx={{ 
												mb: 1,
												fontSize: { xs: "1rem", sm: "1.25rem" }
											}}
										>
											Contact Customer Support
										</Typography>
										<Typography 
											color="text.secondary" 
											sx={{ 
												lineHeight: 1.6,
												fontSize: { xs: "0.875rem", sm: "1rem" }
											}}
										>
											Call us at{" "}
											<Box component="span" sx={{ fontWeight: 600, color: "#667eea" }}>
												+91-8657019346
											</Box>{" "}
											or email{" "}
											<Box component="span" sx={{ fontWeight: 600, color: "#667eea" }}>
												support@stellarview.ai
											</Box>
											.
										</Typography>
									</Box>
								</Box>
							</Paper>

							<Paper
								elevation={0}
								sx={{
									p: { xs: 2, sm: 3 },
									borderRadius: "16px",
									background: "white",
									border: "1px solid rgba(102, 126, 234, 0.1)",
									transition: "all 0.3s ease",
									"&:hover": {
										boxShadow: "0 8px 32px rgba(102, 126, 234, 0.1)",
										transform: "translateY(-2px)"
									}
								}}
							>
								<Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1.5, sm: 2 } }}>
									<Box
										sx={{
											width: { xs: 32, sm: 40 },
											height: { xs: 32, sm: 40 },
											borderRadius: "50%",
											background: "#667eea",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											flexShrink: 0,
											boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)"
										}}
									>
										<Typography 
											variant="h6" 
											color="white" 
											fontWeight={700}
											sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
										>
											2
										</Typography>
									</Box>

									<Box>
										<Typography 
											variant="h6" 
											fontWeight={600} 
											color="text.primary" 
											sx={{ 
												mb: 1,
												fontSize: { xs: "1rem", sm: "1.25rem" }
											}}
										>
											Verify Your Identity
										</Typography>
										<Typography 
											color="text.secondary" 
											sx={{ 
												lineHeight: 1.6,
												fontSize: { xs: "0.875rem", sm: "1rem" }
											}}
										>
											For security purposes, you will be required to mail the evidence of being a registered customer.
											The preferred method is to send the system purchase invoice through your registered email address.
										</Typography>
									</Box>
								</Box>
							</Paper>

							<Paper
								elevation={0}
								sx={{
									p: { xs: 2, sm: 3 },
									borderRadius: "16px",
									background: "white",
									border: "1px solid rgba(102, 126, 234, 0.1)",
									transition: "all 0.3s ease",
									"&:hover": {
										boxShadow: "0 8px 32px rgba(102, 126, 234, 0.1)",
										transform: "translateY(-2px)"
									}
								}}
							>
								<Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1.5, sm: 2 } }}>
									<Box
										sx={{
											width: { xs: 32, sm: 40 },
											height: { xs: 32, sm: 40 },
											borderRadius: "50%",
											background: "#667eea",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											flexShrink: 0,
											boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)"
										}}
									>
										<Typography 
											variant="h6" 
											color="white" 
											fontWeight={700}
											sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
										>
											3
										</Typography>
									</Box>

									<Box>
										<Typography 
											variant="h6" 
											fontWeight={600} 
											color="text.primary" 
											sx={{ 
												mb: 1,
												fontSize: { xs: "1rem", sm: "1.25rem" }
											}}
										>
											Password Change Request
										</Typography>
										<Typography 
											color="text.secondary" 
											sx={{ 
												lineHeight: 1.6,
												fontSize: { xs: "0.875rem", sm: "1rem" }
											}}
										>
											Once your request is approved, send the new password via registered email address and the process
											will be completed.
										</Typography>
									</Box>
								</Box>
							</Paper>
						</Box>
					</Grid>

					<Grid item xs={12} md={5}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								mt: { xs: 2, md: 0 }
							}}
						>
							<Paper
								elevation={0}
								sx={{
									p: { xs: 2, sm: 3 },
									borderRadius: "20px",
									background: "#f8f9fa",
									border: "1px solid rgba(102, 126, 234, 0.1)",
									width: "100%",
									textAlign: "center"
								}}
							>
								<img
									src={ForgotPasswordImage}
									alt="Forgot Password"
									style={{
										width: "100%",
										maxWidth: "300px",
										height: "auto",
										borderRadius: "12px"
									}}
								/>
							</Paper>
						</Box>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};

export default ForgotPasswordDialog;
